import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Highcharts from 'highcharts';
import { Highcharts as HighchartsComponent } from 'core/components';
import QueryModel from 'app/stores/ude/QueryModel';
import styled, { useTheme } from 'styled-components';
import { observer } from 'mobx-react';
import { reaction } from 'mobx';
import { Measurement } from '@kentik/ui-shared/query/query.types';
import { debounce, merge } from 'lodash';
import { formatDimensionValue } from 'app/views/boards/panels/ude/legendUtils';
import { generateHighchartsOptions } from './util/highcharts';

const ChartContainer = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
  position: relative;
`;

interface Props {
  queryModel: QueryModel;
  measurement: Measurement;
  /** Optional callback to receive the Highcharts chart instance (for cross-chart sync). */
  onChartRef?: (chart: Highcharts.Chart | null) => void;
  /** Arbitrary Highcharts options deep-merged into the generated chart config. */
  chartOverrides?: Highcharts.Options;
}

/** Get the objects related to a rowId. This suffix refers to additional yAxis that the object may be associated with. */
function getRelated(chart: Highcharts.Chart, rowId: string) {
  return ['', '-1'].map((suffix) => chart.get(`${rowId}${suffix}`)).filter((obj) => !!obj);
}

function HighchartsDataView({ queryModel, measurement, onChartRef, chartOverrides }: Props) {
  const theme = useTheme();
  const chartRef = useRef<Highcharts.Chart | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerHeight, setContainerHeight] = useState<number | null>(400);

  const { chartOptions, seriesRowIds } = useMemo(() => {
    const result = generateHighchartsOptions(queryModel, measurement, theme, formatDimensionValue);
    const height = result.options.chart?.height ?? containerHeight ?? undefined;
    const baseOptions = {
      ...result.options,
      chart: {
        ...result.options.chart,
        height
      }
    };
    return {
      chartOptions: chartOverrides ? merge({}, baseOptions, chartOverrides) : baseOptions,
      seriesRowIds: result.seriesRowIds
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    queryModel,
    queryModel.effectiveVizLimit,
    queryModel.status,
    queryModel.allRowIds,
    queryModel.lastResultUpdate,
    queryModel.settings,
    theme,
    measurement,
    containerHeight,
    chartOverrides
  ]);

  // Publish charted row IDs to QueryModel so the legend knows which rows have chart series.
  // useLayoutEffect runs synchronously before the browser paints, preventing the 1-frame flash
  // where all rows appear charted (which happened with useEffect's deferred timing).
  useLayoutEffect(() => {
    queryModel.setChartedRowIds(seriesRowIds);
  }, [seriesRowIds, queryModel]);

  // Sync series visibility: observe seriesVisibility map → call series.setVisible()
  useEffect(() => {
    const disposer = reaction(
      () => queryModel.seriesVisibilityMap,
      (visibilityMap) => {
        const chart = chartRef.current;
        if (!chart) {
          return;
        }

        // sankey points are links rather than rows so there is nothing row-indexed to toggle
        if (queryModel.settings.chart.type === 'sankey') {
          return;
        }

        for (const [rowId, visible] of visibilityMap) {
          getRelated(chart, rowId).forEach((obj) => {
            if ('setVisible' in obj) {
              obj.setVisible(visible, false);
            }
          });
        }
        chart.redraw();
      },
      { fireImmediately: true }
    );
    return disposer;
  }, [queryModel]);

  // Sync hovered series: observe hoveredSeriesId → call series.setState()
  useEffect(() => {
    let prevHoveredId: string | null = null;
    const disposer = reaction(
      () => queryModel.hoveredSeriesId,
      (hoveredId) => {
        const chart = chartRef.current;
        if (!chart) {
          return;
        }

        // sankey points are links so highlight every link the hovered row contributes to
        if (queryModel.settings.chart.type === 'sankey') {
          const setPathState = (rowId: string, state: 'hover' | '') => {
            chart.series[0]?.points?.forEach((point) => {
              if (point.custom?.rowIds?.includes(rowId)) {
                point.setState(state);
              }
            });
          };
          if (prevHoveredId) {
            setPathState(prevHoveredId, '');
          }
          if (hoveredId) {
            setPathState(hoveredId, 'hover');
          }
          prevHoveredId = hoveredId;
          return;
        }

        // Unhighlight previous
        if (prevHoveredId) {
          getRelated(chart, prevHoveredId).forEach((obj) => {
            if ('setState' in obj) {
              obj.setState();
            }
          });
          chart.renderer.boxWrapper.removeClass('highcharts-legend-series-active');
        }

        // Highlight current
        if (hoveredId) {
          getRelated(chart, hoveredId).forEach((obj) => {
            if ('setState' in obj) {
              obj.setState('hover');
            }
          });
          chart.renderer.boxWrapper.addClass('highcharts-legend-series-active');
        }

        prevHoveredId = hoveredId;
      },
      { fireImmediately: false }
    );
    return disposer;
  }, [queryModel]);

  const chartCallback = useCallback(
    (chart: Highcharts.Chart) => {
      // If there is an existing chart instance, notify consumer that it is being replaced
      if (chartRef.current && chartRef.current !== chart) {
        onChartRef?.(null);
      }

      chartRef.current = chart;
      onChartRef?.(chart);
    },
    [onChartRef]
  );

  // Notify consumer when the chart is destroyed on unmount
  useEffect(
    () => () => {
      onChartRef?.(null);
    },
    [onChartRef]
  );

  // Use ResizeObserver to track container size and update chart height
  useEffect(() => {
    const container = containerRef.current;
    // we can't resize without a container, and we shouldn't resize charts that have a fixed size
    // note that we can't rely on `chartOptions.chart.height` to tell us that though, due to how `containerHeight` works.
    if (!container || queryModel.settings.chart.type === 'timelineView') {
      return undefined;
    }

    const handleResize = debounce((entries: ResizeObserverEntry[]) => {
      const entry = entries[0];
      if (entry) {
        const newHeight = entry.contentRect.height;
        setContainerHeight(newHeight);
        // Also resize the chart if it exists and is fully initialized
        if (chartRef.current?.options) {
          chartRef.current.setSize(undefined, newHeight, false);
        }
      }
    }, 100);

    const resizeObserver = new ResizeObserver(handleResize);

    resizeObserver.observe(container);

    return () => {
      handleResize.cancel();
      resizeObserver.disconnect();
    };
  }, [queryModel.settings.chart.type]);

  // the key forces the chart to reinitialize when the chart type changes
  return (
    <ChartContainer ref={containerRef}>
      {(chartOptions.chart?.height || containerHeight !== null) && (
        <HighchartsComponent
          key={queryModel.settings.chart.type}
          styledMode={false}
          callback={chartCallback}
          options={chartOptions}
          allowChartUpdate
        />
      )}
    </ChartContainer>
  );
}

export default observer(HighchartsDataView);
